"use client";

import { useRef, useState } from "react";
import clsx from "clsx";
import toast from "react-hot-toast";
import { Button, Spinner } from "@/ui";
import styles from "./ImageUpload.module.css";

interface MutationLike {
  mutate: (
    variables: Record<string, unknown>,
    options?: {
      onSuccess?: () => void;
      onError?: (error: {
        response: { data: { errors: Record<string, string[]> } };
      }) => void;
    },
  ) => void;
}

export interface ImageUploadProps {
  recruiterId: string;
  /** Single-entry map of the recruiter field to its current URL, e.g. `{ logo: url }`. */
  image: Record<string, string | null | undefined>;
  /** Notifies the parent that only an image changed, so it keeps the other edits. */
  updateImage: (patch?: Record<string, string>) => void;
  mutation: MutationLike;
  setFormError: (errors: string[][]) => void;
  shape?: "circle" | "rectangle";
}

export default function ImageUpload({
  recruiterId,
  image,
  updateImage,
  mutation,
  setFormError,
  shape = "circle",
}: ImageUploadProps) {
  const [imageLoading, setImageLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageProperty, imageValue] = Object.entries(image).flat() as [
    string,
    string | null | undefined,
  ];

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImageLoading(true);
    updateImage(); // notifies parent component that only image is being updated
    mutation.mutate(
      {
        id: recruiterId,
        [imageProperty]: file,
      },
      {
        onSuccess: () => {
          toast.success("Image uploaded");
          setImageLoading(false);
          setFormError([]);
        },
        onError: (error) => {
          setImageLoading(false);
          const errors = error?.response?.data?.errors;
          setFormError(
            errors ? Object.values(errors) : [["Failed to upload image. Please try again."]],
          );
        },
      },
    );
  };

  const handleImageRemove = () => {
    const urlProperty = `${imageProperty}_url`;
    mutation.mutate({ id: recruiterId, [urlProperty]: "" });
    updateImage({ [urlProperty]: "" });
  };

  return (
    <div className={styles.row}>
      {imageLoading && (
        <div className={clsx(styles.preview, styles.circle)}>
          <Spinner size="xl" />
        </div>
      )}
      {imageValue && (
        <div className={clsx(styles.preview, styles[shape])}>
          <img src={imageValue} alt={imageProperty} />
        </div>
      )}
      {imageValue && (
        <Button variant="tertiary" size="sm" onClick={handleImageRemove}>
          Remove
        </Button>
      )}
      {!imageValue && !imageLoading && (
        <>
          <Button onClick={() => fileInputRef.current?.click()}>Upload</Button>
          <input
            ref={fileInputRef}
            className={styles.hiddenInput}
            accept="image/*"
            type="file"
            aria-label={`Upload ${imageProperty}`}
            onChange={handleImageUpload}
          />
        </>
      )}
    </div>
  );
}
